import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { inject, Injectable, signal, Type } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import type { DockMode } from './dock-mode';

/**
 * Controls the global {@link DockComponent}: which component it hosts and which mode it is in.
 * Provided in root so dock state persists as the user navigates. On a path-changing navigation,
 * a visible dock collapses: hides entirely on mobile (where it always renders full screen and
 * would otherwise obscure the new page) or drops from full screen to docked elsewhere.
 */
@Injectable({ providedIn: 'root' })
export class DockService {
  private readonly _mode = signal<DockMode>('hidden');
  private readonly _component = signal<Type<unknown> | null>(null);
  private readonly _inputs = signal<Record<string, unknown>>({});

  readonly mode = this._mode.asReadonly();
  readonly component = this._component.asReadonly();
  readonly inputs = this._inputs.asReadonly();

  private readonly isMobile = toSignal(
    inject(BreakpointObserver)
      .observe(Breakpoints.Handset)
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  private previousPath = '';

  constructor() {
    inject(Router)
      .events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => this.onNavigation(event.urlAfterRedirects));
  }

  /**
   * Host `component` in the dock and show it (docked by default). `inputs` are applied to the
   * hosted component via `NgComponentOutletInputs`, so the host can hand a launcher-resolved
   * descriptor straight to the panel without a shared service in the middle.
   */
  open(component: Type<unknown>, mode: DockMode = 'docked', inputs: Record<string, unknown> = {}): void {
    this._component.set(component);
    this._inputs.set(inputs);
    this._mode.set(mode);
  }

  setMode(mode: DockMode): void {
    this._mode.set(mode);
  }

  hide(): void {
    this._mode.set('hidden');
  }

  private onNavigation(url: string): void {
    const path = url.split('?')[0];
    if (path === this.previousPath) {
      return;
    }
    this.previousPath = path;
    const mode = this._mode();
    if (mode === 'hidden') {
      return;
    }
    if (this.isMobile()) {
      this._mode.set('hidden');
    } else if (mode === 'fullscreen') {
      this._mode.set('docked');
    }
  }
}
